import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import PerfectScrollbar from 'react-perfect-scrollbar';
import {
  Box,
  Card,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  List,
  ListItem,
  Popover,
  Typography,
  withStyles,
} from '@material-ui/core';
import { Pagination } from '@material-ui/lab';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { useDispatch, useSelector } from 'react-redux';
import ModalConfirm from 'src/app/components/ModalConfirm';
import { PAGE_SIZE_LIST, messageToastType_const } from 'src/app/constant/config';
import { MESSAGE } from 'src/app/constant/message';
import { useQuery } from 'src/app/hooks/useQuery';
import { showGlobalToast } from 'src/features/uiSlice';
import { resetPasswordCustomer } from 'src/features/userSlice';
import AgencyAccountInfoDialog from '../agency/AgencyAccountInfoDialog';
import EditCustomerAccount from './EditCustomerAccount';

const StyledTableCell = withStyles(theme => ({
  head: {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.common.black,
    whiteSpace: 'nowrap'
  },
  body: {
    fontSize: 14,
    whiteSpace: 'nowrap'
  },
  root: {
    padding: '12px 16px'
  }
}))(TableCell);

const Results = ({
  className,
  totalCustomer,
  listCustomer,
  getListCustomerRef,
  currentParams
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [limit, setLimit] = useState(PAGE_SIZE_LIST);
  const [page, setPage] = useState(1);
  const [params, setParams] = useState({
    page: page,
    page_size: limit
  });

  const query = useQuery();
  const isAgency = useSelector(state => state.authSlice.isAgency);
  const allowEditCustomer = useSelector(state => state.authSlice.allowEditCustomer);
  const canEditCustomer = isAgency && allowEditCustomer;
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [openResetPassword, setOpenResetPassword] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [accountInfo, setAccountInfo] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuCustomer, setMenuCustomer] = useState(null);

  const handleOpenMenu = (event, customer) => {
    setAnchorEl(event.currentTarget);
    setMenuCustomer(customer);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleEditCustomer = () => {
    setEditingCustomer(menuCustomer);
    handleCloseMenu();
  };

  const handleResetPassword = () => {
    setOpenResetPassword(true);
    handleCloseMenu();
  };

  const confirmResetPassword = async () => {
    if (!menuCustomer?.id) return;
    setIsResettingPassword(true);
    const action = await dispatch(resetPasswordCustomer({ id: menuCustomer.id }));
    setIsResettingPassword(false);
    setOpenResetPassword(false);
    if (resetPasswordCustomer.fulfilled.match(action)) {
      setAccountInfo({
        username: menuCustomer.username || menuCustomer.phone || '',
        password: action.payload?.payload?.password || '123456'
      });
    } else {
      // Thunk không tự toast khi lỗi trả về qua axiosInterceptor
      dispatch(
        showGlobalToast({
          message: action.payload?.message || MESSAGE.RESET_PASSWORD_USER_FAIL,
          type: messageToastType_const.error
        })
      );
    }
  };

  useEffect(() => {
    if (currentParams) {
      setParams({
        ...currentParams,
        page: currentParams?.page || 1,
        page_size: currentParams?.page_size || PAGE_SIZE_LIST
      });
    }
  }, [currentParams]);

  useEffect(() => {
    const pageFromQuery = query.get('page');
    const pageSizeFromQuery = query.get('page_size');

    if (pageFromQuery) {
      setParams(prev => ({
        ...prev,
        page: Number(pageFromQuery)
      }));
    }
    if (pageSizeFromQuery) {
      setParams(prev => ({
        ...prev,
        page_size: Number(pageSizeFromQuery)
      }));
    }
  }, [query]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    if (!getListCustomerRef) return;
    const newParams = {
      ...currentParams,
      page: newPage,
      page_size: limit
    }
    setParams(newParams);
    getListCustomerRef(newParams);
  };

  return (
    <>
      {canEditCustomer && editingCustomer && (
        <EditCustomerAccount
          open={Boolean(editingCustomer)}
          customer={editingCustomer}
          currentParams={currentParams}
          onClose={() => setEditingCustomer(null)}
        />
      )}
      <ModalConfirm
        title={MESSAGE.CONFIRM_RESET_PASSWORD_USER}
        content={`Bạn có chắc chắn muốn reset mật khẩu khách hàng "${menuCustomer?.full_name || menuCustomer?.phone || ''}" không ?`}
        textOk="Đồng ý"
        textCancel="Hủy"
        open={openResetPassword}
        setOpen={setOpenResetPassword}
        loading={isResettingPassword}
        callbackOk={confirmResetPassword}
      />
      <AgencyAccountInfoDialog
        open={Boolean(accountInfo)}
        accountInfo={accountInfo}
        onClose={() => setAccountInfo(null)}
        type="customer"
      />
      <Card className={clsx(classes.root, className)}>
        <div>
          <PerfectScrollbar>
            {listCustomer && listCustomer.length ? (
              <Box minWidth={1050}>
                <Table>
                  {listCustomer?.length > 0 && (
                    <TableHead>
                      <TableRow>
                        <StyledTableCell width={250}>
                          Tên khách hàng
                        </StyledTableCell>
                        <StyledTableCell width={250}>
                          Số điện thoại
                        </StyledTableCell>
                        <StyledTableCell width={250}>
                          Tên đăng nhập
                        </StyledTableCell>
                        <StyledTableCell width={250}>Địa chỉ</StyledTableCell>
                        <StyledTableCell width={150}>
                          Thiết bị đã kích hoạt
                        </StyledTableCell>
                        {isAgency && (
                          <StyledTableCell width={80}>Hành động</StyledTableCell>
                        )}
                      </TableRow>
                    </TableHead>
                  )}
                  <TableBody>
                    {listCustomer.map((user) => (
                      <TableRow hover key={user.id}>
                        <StyledTableCell>
                          {user?.full_name || '-'}
                        </StyledTableCell>
                        <StyledTableCell>
                          {user?.phone || '-'}
                        </StyledTableCell>
                        <StyledTableCell>
                          {user?.username || '-'}
                        </StyledTableCell>
                        <StyledTableCell>
                          {user?.address || '-'}
                        </StyledTableCell>
                        <StyledTableCell>
                          {user?.deviceCount || '-'}
                        </StyledTableCell>
                        {isAgency && (
                          <StyledTableCell>
                            <span style={{ color: '#AEB1C5' }}>
                              <MoreVertIcon
                                aria-describedby={`menu-customer-${user.id}`}
                                className="cursor-pointer hover-red"
                                onClick={e => handleOpenMenu(e, user)}
                              />
                            </span>
                            <Popover
                              id={`menu-customer-${user.id}`}
                              className="popover-device"
                              open={Boolean(anchorEl) && menuCustomer?.id === user.id}
                              anchorEl={anchorEl}
                              onClose={handleCloseMenu}
                              anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'left'
                              }}
                              transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right'
                              }}
                            >
                              <List>
                                <ListItem
                                  onClick={handleResetPassword}
                                  className={clsx(
                                    'list-menu-device cursor-pointer',
                                    canEditCustomer && 'border-bottom'
                                  )}
                                >
                                  Reset mật khẩu
                                </ListItem>
                                {canEditCustomer && (
                                  <ListItem
                                    onClick={handleEditCustomer}
                                    className="list-menu-device cursor-pointer"
                                  >
                                    Chỉnh sửa
                                  </ListItem>
                                )}
                              </List>
                            </Popover>
                          </StyledTableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            ) : (
              <div className="text-center pb-5">
                <img src="/static/empty.png" class="justify-content-center" />
                <h4> Danh sách trống </h4>
              </div>
            )}
          </PerfectScrollbar>
        </div>
      </Card>
      {listCustomer && listCustomer.length > 0 && (
        <div class="mt-3 mr-4 float-right">
          <Box
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '15px'
            }}
          >
            <Typography style={{ fontSize: '14px' }}>
              Tổng: {totalCustomer}
            </Typography>
            <Pagination
              count={Math.ceil(totalCustomer / limit)}
              color='primary'
              size="small"
              onChange={handlePageChange}
              page={Number(params.page)}
              showFirstButton
              showLastButton
            />
          </Box>
        </div>
      )}
    </>
  );
};

Results.propTypes = {
  className: PropTypes.string,
  listAgencies: PropTypes.array.isRequired
};

const useStyles = makeStyles(theme => ({
  root: {
    position: 'relative',
    '&.MuiPaper-elevation1': {
      boxShadow: 'none'
    }
  },
  groupAction: {
    display: 'flex',
    alignItems: 'center',
    '& .action': {
      cursor: 'pointer',
      padding: '0 5px',
      '&:hover': {
        color: '#3f51b5'
      }
    }
  },
  minWithColumn: {
    minWidth: '150px'
  }
}));

export default React.memo(Results);
